/**
 * Sharing a room: copy its link (with a toast either way) or hand it to the
 * native share sheet when the platform has one.
 */
import { roomUrl } from '../router';
import { useUiStore } from '../state/uiStore';

/** Copy text to the clipboard; falls back to a hidden textarea + execCommand. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission denied or insecure context: try the legacy path below.
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

export async function copyRoomLink(code: string): Promise<boolean> {
  const url = roomUrl(code);
  const ok = await copyText(url);
  const ui = useUiStore.getState();
  if (ok) ui.toast('Link copied — send it to your opponent', { kind: 'success', id: 'copy' });
  else ui.toast(`Couldn’t copy. The link is ${url}`, { kind: 'warning', id: 'copy', duration: 8000 });
  return ok;
}

export function canShare(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

export async function shareRoom(code: string): Promise<void> {
  if (!canShare()) {
    await copyRoomLink(code);
    return;
  }
  try {
    await navigator.share({ title: 'Wordloom', text: 'Play a game of Wordloom with me', url: roomUrl(code) });
  } catch (err) {
    // Closing the share sheet rejects with AbortError: nothing to report.
    if (err instanceof DOMException && err.name === 'AbortError') return;
    await copyRoomLink(code);
  }
}
